import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Box,
  Paper,
  Typography,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import axiosInstant from '../../../../api';
import config from '../../../../config';
import { adminAttrNetLinks, adminAttrNetLinkDelete } from '../../../../api/endpoints';
import { Button } from '../../../../components/Button/Button';
import { DotLottleLoader } from '../../../../components/Loader';
import { useAffinityCategories } from '../AffinityNetwork/useAffinityGraph';
import { CategoryPicker, FocusOption } from '../AffinityNetwork/CategoryPicker';
import { SubProfessionPicker } from '../AffinityNetwork/SubProfessionPicker';
import { useAttributeGraph, AttributeMode } from './useAttributeGraph';
import { AttributeGraph } from './AttributeGraph';
import { AreaOfFocusPicker } from './AreaOfFocusPicker';
import { AttributeValuePicker } from './AttributeValuePicker';
import { AttributeValueLinkEditor } from './AttributeValueLinkEditor';
import {
  AttributeGraphNode,
  AttributeGraphLink,
  AttributeValueOption,
} from './attribute.types';
import { NODE_COLORS, NODE_TYPE_LABELS, PENDING_COLORS } from './attribute.constants';

interface Props {
  onAfterSave: () => void;
}

interface PendingValue {
  id: number;
  name: string;
  attributeName: string;
}

const Legend: React.FC = () => (
  <Box className="flex flex-wrap items-center gap-3">
    {[
      { c: NODE_COLORS.attribute_value, t: NODE_TYPE_LABELS.attribute_value },
      { c: NODE_COLORS.product_category, t: NODE_TYPE_LABELS.product_category },
      { c: NODE_COLORS.sub_profession, t: NODE_TYPE_LABELS.sub_profession },
      { c: NODE_COLORS.sub_sub_profession, t: NODE_TYPE_LABELS.sub_sub_profession },
      { c: PENDING_COLORS.added, t: 'Pending add' },
      { c: PENDING_COLORS.removed, t: 'Pending remove' },
    ].map(({ c, t }) => (
      <Box key={t} className="flex items-center gap-1.5">
        <Box sx={{ width: 11, height: 11, borderRadius: '50%', bgcolor: c }} />
        <Typography sx={{ fontSize: 12, color: 'var(--text-2)' }}>{t}</Typography>
      </Box>
    ))}
  </Box>
);

const AttributeNetwork: React.FC<Props> = ({ onAfterSave }) => {
  // mode + focus are ONE atomic state (react-force-graph's onNodeClick fires
  // outside React batching, so split setStates could fetch with a stale id).
  const [view, setView] = useState<{ mode: AttributeMode; focus: FocusOption | null }>({
    mode: 'product_category',
    focus: null,
  });
  const { mode, focus } = view;
  // Staged edits relative to the loaded link set, keyed by attribute_value id.
  const [pendingAdds, setPendingAdds] = useState<Map<number, PendingValue>>(new Map());
  const [pendingRemoves, setPendingRemoves] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);

  const { data, loading, error, refetch } = useAttributeGraph(mode, focus?.id ?? null);
  const { categories } = useAffinityCategories();

  // Every editable mode links the focused entity to attribute_value nodes.
  const editable = mode !== 'attribute_value';
  const dirty = pendingAdds.size > 0 || pendingRemoves.size > 0;

  const clearPending = () => {
    setPendingAdds(new Map());
    setPendingRemoves(new Set());
  };

  const confirmDiscard = () => !dirty || window.confirm('Discard unsaved attribute-link changes?');

  const switchMode = (next: AttributeMode) => {
    if (!next || next === mode) return;
    if (!confirmDiscard()) return;
    clearPending();
    setView({ mode: next, focus: null });
  };

  const pickFocus = (next: FocusOption | null) => {
    if (!confirmDiscard()) return;
    clearPending();
    setView((v) => ({ ...v, focus: next }));
  };

  // Existing (server-side) attribute_value ids for the loaded focus entity.
  const existingIds = useMemo(() => {
    const ids = new Set<number>();
    (data?.nodes || []).forEach((n) => {
      if (n.id === data?.root.id) return;
      if (n.attributeValueId != null) ids.add(n.attributeValueId);
    });
    return ids;
  }, [data]);

  const linkedValueIds = useMemo(() => {
    const ids = new Set<number>();
    existingIds.forEach((id) => {
      if (!pendingRemoves.has(id)) ids.add(id);
    });
    pendingAdds.forEach((_, id) => ids.add(id));
    return ids;
  }, [existingIds, pendingRemoves, pendingAdds]);

  // Build the graph the user sees.
  const { nodes, links } = useMemo(() => {
    const outNodes: AttributeGraphNode[] = [];
    const outLinks: AttributeGraphLink[] = [];
    if (!data) return { nodes: outNodes, links: outLinks };

    // Read-only attribute-option hub: render the server graph verbatim.
    if (!editable) {
      return {
        nodes: (data.nodes || []).map((n) => ({ ...n })),
        links: (data.links || []).map((l) => ({ ...l })),
      };
    }

    const rootId = data.root.id;
    (data.nodes || []).forEach((n) => {
      if (n.id === rootId) {
        outNodes.push({ ...n });
        return;
      }
      const removed = n.attributeValueId != null && pendingRemoves.has(n.attributeValueId);
      outNodes.push({ ...n, pendingState: removed ? 'removed' : undefined });
      outLinks.push({ source: rootId, target: n.id, pendingState: removed ? 'removed' : undefined });
    });

    pendingAdds.forEach((nb, id) => {
      const nodeId = `av-${id}`;
      outNodes.push({
        id: nodeId,
        type: 'attribute_value',
        name: nb.name,
        attributeValueId: id,
        attributeName: nb.attributeName,
        pendingState: 'added',
      });
      outLinks.push({ source: rootId, target: nodeId, pendingState: 'added' });
    });

    return { nodes: outNodes, links: outLinks };
  }, [data, editable, pendingRemoves, pendingAdds]);

  const drillInto = (node: AttributeGraphNode) => {
    if (node.type === 'product_category' && node.categoryId != null) {
      setView({ mode: 'product_category', focus: { id: node.categoryId, name: node.name } });
    } else if (node.type === 'sub_profession' && node.subProfessionId != null) {
      setView({ mode: 'sub_profession', focus: { id: node.subProfessionId, name: node.name } });
    } else if (node.type === 'sub_sub_profession' && node.subSubProfessionId != null) {
      setView({ mode: 'sub_sub_profession', focus: { id: node.subSubProfessionId, name: node.name } });
    }
  };

  const handleNodeClick = (node: AttributeGraphNode) => {
    // Read-only hub: a click on an entity node drills into its editor.
    if (!editable) {
      if (node.id === data?.root.id) return;
      if (!confirmDiscard()) return;
      clearPending();
      drillInto(node);
      return;
    }

    // Editable modes: toggle the attribute_value link.
    if (data && node.id === data.root.id) return;
    const id = node.attributeValueId;
    if (id == null) return;

    if (pendingAdds.has(id)) {
      setPendingAdds((prev) => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
      return;
    }
    if (existingIds.has(id)) {
      setPendingRemoves((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    }
  };

  const handleAddValue = (val: AttributeValueOption) => {
    if (existingIds.has(val.id)) {
      // Re-adding an existing-but-removed link cancels the removal.
      setPendingRemoves((prev) => {
        const next = new Set(prev);
        next.delete(val.id);
        return next;
      });
      return;
    }
    setPendingAdds((prev) =>
      new Map(prev).set(val.id, { id: val.id, name: val.name, attributeName: val.attributeName }));
  };

  const save = async () => {
    // Staged ids are derived from the loaded graph — never write without one
    // (e.g. after a fetch error replaced it), the pairs would be meaningless.
    if (!focus || !data) return;
    setSaving(true);
    try {
      const requests: Promise<unknown>[] = [];
      pendingAdds.forEach((_, id) => {
        requests.push(axiosInstant.post(`${config.API_URL}${adminAttrNetLinks}`, {
          entity_type: mode,
          entity_id: focus.id,
          attribute_value_id: id,
        }));
      });
      pendingRemoves.forEach((id) => {
        requests.push(axiosInstant.delete(`${config.API_URL}${adminAttrNetLinkDelete(mode, focus.id, id)}`));
      });
      await Promise.all(requests);
      toast.success('Attribute links saved — run a recompute to apply them to scores');
      clearPending();
      await refetch();
      onAfterSave();
    } catch (err) {
      toast.error('Failed to save attribute links');
    } finally {
      setSaving(false);
    }
  };

  const emptyMessages: Record<AttributeMode, string> = {
    product_category: 'Pick a product category to view and edit its attribute options.',
    sub_profession: 'Pick a sub-profession to view and edit its attribute options.',
    sub_sub_profession: 'Pick an area of focus to view and edit its attribute options.',
    attribute_value: 'Pick an attribute option to view the categories and professions linked to it.',
  };
  const emptyMsg = emptyMessages[mode];

  return (
    <Paper sx={{ p: 3 }}>
      <Box className="flex flex-wrap items-center justify-between gap-3 pb-1">
        <Typography fontWeight={600}>Attribute network</Typography>
        {editable && (
          <Box className="flex items-center gap-3">
            <Typography color="var(--text-2)" sx={{ fontSize: 13 }}>
              {dirty ? `${pendingAdds.size} add · ${pendingRemoves.size} remove pending` : 'No pending changes'}
            </Typography>
            <Button
              onClick={() => clearPending()}
              disabled={!dirty || saving}
              className="!px-4 !bg-surface !text-accent-text !border !border-accent"
            >
              <Typography>Reset</Typography>
            </Button>
            <Button onClick={save} disabled={!dirty || saving} className="!px-5">
              <Typography>{saving ? 'Saving…' : 'Save links'}</Typography>
            </Button>
          </Box>
        )}
      </Box>
      <Typography color="var(--text-2)" sx={{ fontSize: 14, pb: 2 }}>
        Explore the product-category ⇄ attribute-option ⇄ profession network. Edit
        an entity&apos;s attribute options from the category, sub-profession or area-of-focus
        side; the attribute-option view is a read-only hub (click any node to drill
        in). Nothing is written until you Save.
      </Typography>

      <ToggleButtonGroup
        size="small"
        exclusive
        value={mode}
        onChange={(_, v) => v && switchMode(v)}
        sx={{ mb: 2 }}
      >
        <ToggleButton value="product_category">By category</ToggleButton>
        <ToggleButton value="sub_profession">By sub-profession</ToggleButton>
        <ToggleButton value="sub_sub_profession">By area of focus</ToggleButton>
        <ToggleButton value="attribute_value">By attribute option</ToggleButton>
      </ToggleButtonGroup>

      <Divider sx={{ mb: 2 }} />

      <Box className="flex flex-col gap-4 md:flex-row">
        {/* Left rail: picker + (editable) add editor */}
        <Box sx={{ width: '100%', maxWidth: 320 }} className="flex flex-col gap-4 flex-shrink-0">
          {mode === 'product_category' && (
            <CategoryPicker categories={categories} value={focus} onChange={pickFocus} />
          )}
          {mode === 'sub_profession' && (
            <SubProfessionPicker
              value={focus ? { id: focus.id, name: focus.name, professionName: '' } : null}
              onChange={(v) => pickFocus(v ? { id: v.id, name: v.name } : null)}
            />
          )}
          {mode === 'sub_sub_profession' && (
            <AreaOfFocusPicker
              value={focus ? { id: focus.id, name: focus.name, professionName: '' } : null}
              onChange={(v) => pickFocus(v ? { id: v.id, name: v.name } : null)}
            />
          )}
          {mode === 'attribute_value' && (
            <AttributeValuePicker value={focus} onChange={pickFocus} />
          )}

          {editable && focus && (
            <AttributeValueLinkEditor
              linkedValueIds={linkedValueIds}
              onAdd={handleAddValue}
              disabled={saving}
            />
          )}
        </Box>

        {/* Graph */}
        <Box className="flex flex-col gap-2" sx={{ flex: 1, minWidth: 0 }}>
          <Legend />
          {!focus && (
            <Box
              className="flex items-center justify-center"
              sx={{ height: 520, border: '1px dashed var(--border)', borderRadius: 1.5, color: 'var(--muted)' }}
            >
              <Typography>{emptyMsg}</Typography>
            </Box>
          )}
          {focus && loading && (
            <Box className="flex items-center justify-center" sx={{ height: 520 }}>
              <DotLottleLoader />
            </Box>
          )}
          {focus && !loading && error && (
            <Box
              className="flex flex-col items-center justify-center gap-3"
              sx={{ height: 520, border: '1px dashed var(--border)', borderRadius: 1.5, color: 'var(--muted)' }}
            >
              <Typography>Failed to load the attribute graph.</Typography>
              <Button onClick={() => refetch()} className="!px-4">
                <Typography>Retry</Typography>
              </Button>
            </Box>
          )}
          {focus && !loading && !error && data && (
            <>
              {editable && nodes.filter((n) => n.id !== data.root.id).length === 0 && !dirty && (
                <Typography color="var(--text-2)" sx={{ fontSize: 13 }}>
                  No attribute options linked yet — add the first one on the left.
                </Typography>
              )}
              <AttributeGraph
                nodes={nodes}
                links={links}
                rootId={data.root.id}
                onNodeClick={handleNodeClick}
              />
            </>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default AttributeNetwork;
